/** Sync-pill inspector (2026-07-19). When the server definitively rejects
 * queued changes they move to the outbox's dead list — before this modal the
 * pill just said "kept aside, tell Claude" and the ops sat invisible. Clicking
 * the pill now shows WHAT was rejected and WHY (the door's own reason, kept
 * per-op since the Phase-26 batch), with two ways out:
 *   Retry all — re-queue everything and drain; rejects caused by since-fixed
 *               rules (e.g. PARTNERS before Phase 26) simply deliver now, and
 *               anything still refused returns here with a fresh reason.
 *   Discard   — drop them for good (the edits stay lost, the pill clears). */

export interface DeadOpView {
  s: string;
  op: string;
  k: string;
  err?: string;
}

const SECTION_LABEL: Record<string, string> = {
  lead_fields: "Property details",
  leads: "Property",
  tasks: "Task",
  deals: "Deal",
  calendar: "Calendar",
  tombs: "Deletion",
  wholesale: "Wholesale",
  saved_views: "Saved view",
  contacts: "Contact",
  settings: "Settings",
  scope_meta: "SOW",
  scope_cats: "SOW category",
  scope_items: "SOW item",
  materials: "Material",
  material_categories: "Material category",
};

/** Friendly names for the org-settings ledgers (the `k` of settings puts). */
const SETTINGS_KEY_LABEL: Record<string, string> = {
  TXNEXP: "Deal expenses",
  EXP2: "Property expenses",
  COMPANY_EXP: "Company expenses",
  TXN_CONTRACTORS: "Contractors",
  INVESTORS: "Investors",
  BUYERS: "Buyers",
  WHOLESALER_QUEUE: "Wholesaler queue",
  PARTNERS: "Partner access",
  OUTCOME_LOG: "Outcome log",
  SOW_TEMPLATES: "SOW templates",
};

const VERB: Record<string, string> = { put: "save", patch: "edit", del: "delete", add: "add", group: "save" };

export function deadOpLabel(o: DeadOpView): string {
  if (o.s === "settings") return (SETTINGS_KEY_LABEL[o.k] ?? o.k) + " save";
  const key = o.k.length > 44 ? o.k.slice(0, 44) + "…" : o.k;
  // tombs: `add` plants a delete-tombstone, `del` lifts one (restore)
  if (o.s === "tombs") return `${o.op === "add" ? "Delete" : "Restore"} — ${key}`;
  const sec = SECTION_LABEL[o.s] ?? o.s;
  return `${sec} ${VERB[o.op] ?? o.op} — ${key}`;
}

export function DeadOpsModal({
  ops,
  onRetry,
  onDiscard,
  onClose,
}: {
  ops: DeadOpView[];
  onRetry: () => void;
  onDiscard: () => void;
  onClose: () => void;
}) {
  return (
    <div className="calmodalwrap" onClick={onClose}>
      <div className="calmodal deadmodal" onClick={(e) => e.stopPropagation()}>
        <div className="calmodalhead">
          <h2>Rejected changes ({ops.length})</h2>
          <button className="taskdel" onClick={onClose}>×</button>
        </div>
        <div className="calmodalbody">
          <p className="sub deadintro">
            The server refused these saves — they're kept aside and nothing else is blocked.
            <b> Retry all</b> sends them again (rejects caused by a since-fixed rule just go through);
            <b> Discard</b> drops them for good.
          </p>
          <div className="deadlist">
            {ops.map((o, i) => (
              <div key={i} className="deadrow">
                <div className="deadwhat">{deadOpLabel(o)}</div>
                <div className="deadwhy">{o.err ?? "No reason recorded — this one was rejected before v2 kept reasons."}</div>
              </div>
            ))}
          </div>
        </div>
        <div className="calmodalfoot">
          <button className="out" onClick={onDiscard}>Discard all</button>
          <button className="btn primary" onClick={onRetry}>Retry all</button>
        </div>
      </div>
    </div>
  );
}
